import React from "react";
import {
  BadgeCheck,
  Calendar,
  MapPin,
  ExternalLink,
  Building2,
} from "lucide-react";
import { Experience } from "../sample.data";
import { getVisibleSortedItems } from "../utils";

interface ExperienceListProps {
  experiences: Experience[];
}

export const ExperienceList: React.FC<ExperienceListProps> = ({
  experiences,
}) => {
  const visibleExperiences = getVisibleSortedItems(experiences);

  if (visibleExperiences.length === 0) return null;

  return (
    <div className="space-y-6">
      {visibleExperiences.map((experience) => (
        <article
          key={experience.id}
          className="group relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-300 overflow-hidden"
        >
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-purple-50/50 dark:from-blue-900/10 dark:via-transparent dark:to-purple-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

          <div className="relative p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4">
              <div className="flex items-start gap-3 sm:gap-4 flex-1">
                {/* Company icon placeholder */}
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-xl flex items-center justify-center">
                  {experience.companyLogo ? (
                    <img
                      src={experience.companyLogo}
                      alt={experience.company}
                      className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg object-cover"
                    />
                  ) : (
                    <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 dark:text-gray-400" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-1 leading-tight">
                    {experience.role}
                  </h3>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-gray-600 dark:text-gray-400 mb-3 sm:mb-2">
                    <span className="font-medium text-sm sm:text-base">
                      {experience.company}
                    </span>
                    {experience.type && (
                      <>
                        <span className="text-gray-400 hidden sm:inline">
                          •
                        </span>
                        <span className="text-xs sm:text-sm capitalize bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full w-fit">
                          {experience.type}
                        </span>
                      </>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-3 sm:mb-4">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                      <span className="truncate">
                        {experience.start} – {experience.end}
                      </span>
                    </span>
                    {experience.location && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="truncate">{experience.location}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {experience.link && (
                <a
                  href={experience.link}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-shrink-0 self-start mt-2 sm:mt-0 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>

            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4 text-sm sm:text-base">
              {experience.summary}
            </p>

            {experience.achievements && experience.achievements.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <BadgeCheck className="h-4 w-4 text-green-600 flex-shrink-0" />
                  Key Achievements
                </h4>
                <ul className="space-y-2">
                  {experience.achievements.map(
                    (achievement, achievementIndex) => (
                      <li
                        key={achievementIndex}
                        className="flex items-start gap-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                        <span className="flex-1">{achievement}</span>
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}

            {experience.technologies && experience.technologies.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {experience.technologies.map((tech) => (
                  <span
                    key={tech}
                    className="px-2 py-1 sm:px-3 sm:py-1 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-full border border-gray-200 dark:border-gray-700"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Subtle bottom border accent */}
          <div className="h-1 bg-blue-500 opacity-20"></div>
        </article>
      ))}
    </div>
  );
};
